import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Alert, Snackbar } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { AdminCompanyListItem, Company, CreateCompanyRequest, UpdateCompanyRequest, SearchParams, SubscriptionStatusDto, UpdateSubscriptionRequest } from '../types';
import { apiClient } from '../services/api';
import { CompanyList, CompanyDialog, SubscriptionDialog, CompanyNoteDialog } from '../features/admin';
import { CompanyListRef } from '../features/admin';
import { useLanguage } from '../contexts/LanguageContext';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';
import { useCompanyMutations } from '../hooks/useCompanyMutations';
import { errorMessage } from '../utils/errorMessage';

const fetchCompaniesPage = (params: SearchParams): Promise<{ content: AdminCompanyListItem[]; totalElements: number; totalPages: number; number: number; size: number }> => apiClient.getAdminCompaniesPaginated(params);

const Companies: React.FC = () => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const companyListRef = useRef<CompanyListRef>(null);
  const [searchParams, setSearchParams] = useState<SearchParams>(() => {
    const page = Number(urlSearchParams.get('page'));
    const size = Number(urlSearchParams.get('size'));
    const expires = Number(urlSearchParams.get('expiresWithinDays'));
    const rawDirection = urlSearchParams.get('direction');
    return {
      name: urlSearchParams.get('search') || undefined,
      effectiveStatus: (urlSearchParams.get('effectiveStatus') || undefined) as SearchParams['effectiveStatus'],
      usageStatus: (urlSearchParams.get('usageStatus') || undefined) as SearchParams['usageStatus'],
      expiresWithinDays: expires === 7 || expires === 30 ? expires : undefined,
      page: Number.isInteger(page) && page >= 0 ? page : 0,
      size: Number.isInteger(size) && size >= 1 && size <= 100 ? size : 10,
      sort: urlSearchParams.get('sort') || 'name',
      direction: rawDirection === 'desc' ? 'desc' : 'asc',
    };
  });

  useEffect(() => {
    const next = new URLSearchParams();
    if (searchParams.name) next.set('search', searchParams.name);
    if (searchParams.effectiveStatus) next.set('effectiveStatus', searchParams.effectiveStatus);
    if (searchParams.usageStatus) next.set('usageStatus', searchParams.usageStatus);
    if (searchParams.expiresWithinDays) next.set('expiresWithinDays', String(searchParams.expiresWithinDays));
    if (searchParams.page !== undefined) next.set('page', String(searchParams.page));
    if (searchParams.size !== undefined) next.set('size', String(searchParams.size));
    if (searchParams.sort) next.set('sort', searchParams.sort);
    if (searchParams.direction) next.set('direction', searchParams.direction);
    setUrlSearchParams(next, { replace: true });
  }, [searchParams, setUrlSearchParams]);

  // Dialog states
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [subscriptionCompany, setSubscriptionCompany] = useState<Company | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionStatusDto | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionSaving, setSubscriptionSaving] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
  const [notesCompany, setNotesCompany] = useState<Company | null>(null);
  
  // Notification states
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const companiesQuery = usePaginatedQuery<AdminCompanyListItem>(searchParams, fetchCompaniesPage, t('companies.loadError'), 'name', true, 'companies');
  const { saveCompanyMutation } = useCompanyMutations();

  const showNotification = useCallback((message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setNotification({ open: true, message, severity });
  }, []);

  const hideNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  useEffect(() => {
    if (companiesQuery.error) {
      showNotification(companiesQuery.error, 'error');
    }
  }, [companiesQuery.error, showNotification]);

  // Search handlers
  const handleSearch = (name?: string) => {
    setSearchParams(prev => ({
      ...prev,
      name,
      page: 0 // Reset to first page when searching
    }));
  };

  // Handle pagination - unified interface for TanStack React Table  
  const handlePaginationModelChange = (model: { page: number; pageSize: number }) => {
    setSearchParams(prev => ({
      ...prev,
      page: prev.size !== model.pageSize ? 0 : model.page,
      size: model.pageSize,
    }));
  };

  const handleSortChange = (field?: string, direction?: 'asc' | 'desc') => {
    setSearchParams(prev => ({ ...prev, sort: field, direction, page: 0 }));
  };

  const handleFilterChange = (filters: Pick<SearchParams, 'effectiveStatus' | 'expiresWithinDays' | 'usageStatus'>) => {
    setSearchParams(prev => ({ ...prev, ...filters, page: 0 }));
  };

  const handleAddCompany = () => {
    setSelectedCompany(null);
    setCompanyDialogOpen(true);
  };

  const handleEditCompany = (company: Company) => {
    setSelectedCompany(company);
    setCompanyDialogOpen(true);
  };

  const handleManageSubscription = async (company: Company) => {
    setSubscriptionCompany(company);
    setSubscriptionDialogOpen(true);
    setSubscriptionLoading(true);
    setSubscriptionError(null);
    try {
      setSubscription(await apiClient.getCompanySubscription(company.id));
    } catch (error: unknown) {
      setSubscriptionError(errorMessage(error, t('subscription.loadFailed')));
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleCompanyNotes = (company: Company) => setNotesCompany(company);

  const handleSubmitSubscription = async (request: UpdateSubscriptionRequest) => {
    if (!subscriptionCompany) return;
    setSubscriptionSaving(true);
    setSubscriptionError(null);
    try {
      const updated = await apiClient.updateCompanySubscription(subscriptionCompany.id, request);
      setSubscription(updated);
      await queryClient.invalidateQueries({ queryKey: ['admin'] });
      showNotification(t('subscription.saved'), 'success');
      setSubscriptionDialogOpen(false);
    } catch (error: unknown) {
      setSubscriptionError(errorMessage(error, t('subscription.saveFailed')));
      throw error;
    } finally {
      setSubscriptionSaving(false);
    }
  };

  const handleSubmitCompany = async (data: CreateCompanyRequest | UpdateCompanyRequest) => {
    try {
      await saveCompanyMutation.mutateAsync({ id: selectedCompany?.id, data });
      if (selectedCompany) {
        showNotification(t('companies.updateSuccess'));
      } else {
        showNotification(t('companies.createSuccess'));
      }
      
      setCompanyDialogOpen(false);
    } catch (error: unknown) {
      showNotification(errorMessage(error, t('companies.saveError')), 'error');
      throw error;
    }
  };

  const handleCloseDialog = () => {
    setCompanyDialogOpen(false);
    setSelectedCompany(null);
  };

  return (
    <Container maxWidth={false} disableGutters>
      <CompanyList
        ref={companyListRef}
        paginatedData={companiesQuery.data}
        loading={companiesQuery.isLoading}
        searchParams={searchParams}
        onSearch={handleSearch}
        onPaginationModelChange={handlePaginationModelChange}
        onSortChange={handleSortChange}
        onEdit={handleEditCompany}
        onAdd={handleAddCompany}
        onManageSubscription={handleManageSubscription}
        onManageNotes={handleCompanyNotes}
        onFilterChange={handleFilterChange}
      />

      <CompanyDialog
        open={companyDialogOpen}
        company={selectedCompany}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitCompany}
        isLoading={saveCompanyMutation.isPending}
      />

      <SubscriptionDialog
        open={subscriptionDialogOpen}
        company={subscriptionCompany}
        subscription={subscription}
        loading={subscriptionLoading}
        saving={subscriptionSaving}
        error={subscriptionError}
        onClose={() => {
          setSubscriptionDialogOpen(false);
          setSubscriptionCompany(null);
          setSubscription(null);
          setSubscriptionError(null);
        }}
        onSubmit={handleSubmitSubscription}
      />

      <CompanyNoteDialog open={!!notesCompany} company={notesCompany} onClose={() => setNotesCompany(null)} onSaved={() => showNotification('Not eklendi')} />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={hideNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={hideNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Companies;
